import React, { useState } from 'react';
import { Flex, Text, Tooltip } from '@chakra-ui/react';
import {
  faBoxArchive,
  faInfoCircle,
  faRefresh,
  faTriangleExclamation
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Button, ButtonIcon } from '@morressier/alembic';

import {
  IAuthorPhase,
  isAuthorPhase,
  IWorkPhase
} from '@publishing-engine/domain/models';
import ContextMenu from 'src/components/Submission/ContextMenu';
import { ValueOf } from 'src/utils/types';
import { IGetArticleWorkflowResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import {
  DecisionMenuActions,
  getModalForDecision,
  IDecisionModal
} from '../../decisionModals/decisionModalHelper';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import dateFormat from 'dateformat';
import { getPhaseContent } from 'src/utils/phaseCopy';
import { getActionDescription } from 'src/utils/displayHelpers';
import { getActionLabel } from 'src/utils/displayHelpers';
import ProtectedActionButton from 'src/components/ProtectedAction/Button';
import useAuthAccessQuery from 'src/hooks/useAuthAccessQuery';
import { useQuery } from '@tanstack/react-query';
import { fetchOrganizationAndJournalsInformation } from '../../../../api/queries';
import { timeFormatDeadlineDate } from '../../../../utils/date';
import { useNavigate } from 'react-router-dom';

type WorkflowStateConfig = ValueOf<
  IGetArticleWorkflowResponse['articleWorkflow']['transitionGraph']['submission']['states']
>;

type Props = {
  phaseName: IAuthorPhase;
  workflowStateConfig: WorkflowStateConfig;
  showingDecisionsForPhase: null | string;
  setShowingDecisionsForPhase: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  setShowingDecisionOptions: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  submissionContextId: string;
  setActiveTab: (tab: any) => void;
};

const getBadgeContent = (
  isInAuthorPhase: boolean,
  hasDeadlinePassed: boolean,
  withdrawalRequested: boolean
) => {
  if (withdrawalRequested) {
    return {
      icon: faBoxArchive,
      text: 'Withdrawal Requested'
    };
  }

  if (hasDeadlinePassed && isInAuthorPhase) {
    return {
      icon: faTriangleExclamation,
      text: 'Overdue'
    };
  }

  return {
    icon: faRefresh,
    text: 'In Progress'
  };
};

export const WorkPhaseSection = ({
  phaseName,
  workflowStateConfig,
  setShowingDecisionsForPhase,
  setActiveTab,
  setShowingDecisionOptions
}: Props) => {
  const {
    workflowResponse,
    activeRevision,
    organizationId,
    submissionContext
  } = useSubmissionData();
  const { submissionId, submissionContextId } = workflowResponse;
  const { actions } = workflowStateConfig;
  const isInAuthorPhase = isAuthorPhase(
    workflowResponse.workflowData.currentState
  );

  const reviewInformationLink =
    submissionContext?.config?.reviewInformationLink || '';

  const [showingDecisionModal, setShowingDecisionModal] = useState<
    string | null
  >(null);

  let Modal: IDecisionModal | undefined = undefined;
  if (showingDecisionModal) {
    Modal = getModalForDecision(showingDecisionModal as DecisionMenuActions);
  }

  const actionsByDisplay = Object.groupBy(
    actions,
    action => action.displayProperties.location
  );

  const hasDecisions =
    actionsByDisplay.decisionMenu && actionsByDisplay.decisionMenu.length > 0;

  const { isAllowed, isLoading } = useAuthAccessQuery({
    relation: 'author',
    entityType: 'submission',
    entityId: submissionId
  });

  const contextActions = actionsByDisplay.contextMenu ?? [];

  const contextMenuActions = isAllowed
    ? contextActions.map(action => ({
        name: action.name,
        action: () => setShowingDecisionModal(action.name),
        label: getActionLabel(action.name),
        description: getActionDescription(action.name) ?? ''
      }))
    : [];
  const { data: organizationData } = useQuery({
    queryKey: ['organization'],
    queryFn: () =>
      organizationId
        ? fetchOrganizationAndJournalsInformation(organizationId)
        : null
  });

  if (phaseName === 'revision') {
    contextMenuActions.push({
      name: 'AuthorSubmission#revisionGuidelinePolicyLink',
      action: () => {
        window.open(reviewInformationLink, '_blank');
      },
      label: `View ${organizationData?.organization.name}  Revision Guidelines`,
      description: ''
    });
  }

  const currentPhaseDeadline = workflowResponse.workflowData.currentDeadline
    ? new Date(workflowResponse.workflowData.currentDeadline)
    : undefined;

  const hasDeadlinePassed = currentPhaseDeadline
    ? new Date() > currentPhaseDeadline
    : false;

  const withdrawalRequested =
    !!workflowResponse.workflowData.withdrawal?.requested;

  const phaseContent = getPhaseContent(
    workflowResponse.workflowData.currentState as IWorkPhase,
    'author',
    {
      severity: activeRevision?.severity || 'minor',
      deadline: dateFormat(currentPhaseDeadline, 'mmm d, yyyy'),
      dateSent: dateFormat(
        activeRevision?.dateCompleted || activeRevision?.dateEntered,
        'mmm d, yyyy'
      )
    }
  );

  const badgeContent = getBadgeContent(
    isInAuthorPhase,
    hasDeadlinePassed,
    withdrawalRequested
  );

  const checkDeadlineDisplay = () => {
    const isDeadline =
      isInAuthorPhase ||
      workflowResponse.workflowData.currentState === 'withdrawalRequested'
        ? workflowResponse.workflowData.currentDeadline
        : undefined;
    return isDeadline;
  };

  const isDeadlineVisible = checkDeadlineDisplay();
  const decisionActionNames = workflowStateConfig.actions
    .filter(
      actionReference =>
        actionReference.displayProperties.location === 'decisionMenu'
    )
    .map(actionReference => actionReference.name) as DecisionMenuActions[];

  const { decisionMenuConfig } = submissionContext?.config ?? {};
  const navigate = useNavigate();
  const enabledPhase = ['revision', 'immediateRevision'];
  return (
    <>
      {showingDecisionModal && Modal ? (
        <Modal
          onClose={() => {
            setShowingDecisionModal(null);
            setShowingDecisionsForPhase(null);
          }}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={submissionContextId}
          setActiveTab={setActiveTab}
        />
      ) : null}

      <>
        <Flex
          flexDirection="column"
          width="100%"
          backgroundColor="surfacePrimaryFill"
        >
          {/* ------ Current State Name ----- */}
          <Flex flexDirection="column">
            <Flex
              alignItems="center"
              gap="2"
              backgroundColor="surfaceSecondaryFill"
              padding={4}
              justifyContent={'space-between'}
            >
              <Flex alignItems="center">
                <Text textStyle="headingItem">{phaseContent.heading}</Text>

                <Tooltip
                  hasArrow
                  shouldWrapChildren
                  placement="top"
                  label={phaseContent.description}
                  borderRadius={4}
                >
                  <ButtonIcon
                    size="small"
                    ariaLabel="Help icon for current phase"
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                  />
                </Tooltip>
              </Flex>

              {!!contextMenuActions.length && !isLoading && (
                <ContextMenu actions={contextMenuActions} />
              )}
              {/* --- Current Phase Deadline */}
            </Flex>
          </Flex>

          <Flex flexDirection="column" padding={4} gap={4}>
            {isDeadlineVisible && currentPhaseDeadline ? (
              <Flex
                gap={2}
                textColor={hasDeadlinePassed ? 'errorFillInverse' : 'black'}
              >
                <Badge
                  size="context"
                  variant={
                    hasDeadlinePassed && isInAuthorPhase
                      ? 'caution'
                      : 'attention'
                  }
                >
                  <Flex gap={1} alignItems="center">
                    <FontAwesomeIcon icon={badgeContent.icon} />
                    <Text>{badgeContent.text}</Text>
                  </Flex>
                </Badge>
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  backgroundColor={
                    hasDeadlinePassed
                      ? 'errorFillInverse'
                      : 'attentionSecondaryFill'
                  }
                  color={
                    hasDeadlinePassed
                      ? 'neutralPrimaryContentInverse'
                      : 'successPrimaryFill'
                  }
                  borderRadius="small"
                ></Flex>
                Due {timeFormatDeadlineDate(currentPhaseDeadline)}
              </Flex>
            ) : null}

            {/* Actions */}
            <Flex gap={2} flexDirection={'column'} justifyContent="flex-end">
              {hasDecisions &&
                (enabledPhase.includes(phaseName) ? (
                  <>
                    {decisionActionNames.map(action => {
                      return (
                        <Button
                          onClick={() => {
                            decisionMenuConfig?.[action]?.label ===
                            'Revise Submission'
                              ? navigate('?submissionType=revision')
                              : setShowingDecisionModal(action);
                          }}
                          variant={
                            decisionMenuConfig?.[action]?.label ===
                            'Revise Submission'
                              ? 'primary'
                              : 'secondary'
                          }
                        >
                          {decisionMenuConfig?.[action]?.label || action}
                        </Button>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <Flex flexDirection={'column'} flex={1} gap={2}>
                      {decisionActionNames.map(action => {
                        return (
                          <>
                            <ProtectedActionButton
                              relation="author"
                              entityType="submission"
                              entityId={submissionId}
                              size="context"
                              shouldWrapChildren={false}
                              variant="secondary"
                              as="button"
                              onClick={() => setShowingDecisionOptions(action)}
                            >
                              {decisionMenuConfig?.[action]?.label || action}
                            </ProtectedActionButton>{' '}
                          </>
                        );
                      })}
                    </Flex>
                  </>
                ))}
            </Flex>
          </Flex>
        </Flex>
      </>
    </>
  );
};
